/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Common/StackedColumnChartDataSelector" />
import { gridLineColor } from "Analytics/Shared/Common/Constants";
import { adjustDataForAllPeriod, IDataForDate } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { getStackedColumnChartData } from "Pipeline/Analytics/Common/ChartHelper/StackedColumnChartDataSelector";
import { maxItemsToShowInCharts } from "Pipeline/Analytics/Common/Constants";
import { colors } from "Pipeline/Analytics/Common/Constants";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import { IAnalyticsChartProps } from "Pipeline/Analytics/Common/Types";
import { IDailyTaskFailureData, ITaskFailure } from "Pipeline/Analytics/PipelineOutcome/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Resources";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";

interface ITaskDataValue {
    totalFailures: number;
    dailyFailures: { [date: string]: number };
}

export function getStackedColumnChartProps(
    pageContext: IVssPageContext,
    data: IDailyTaskFailureData[],
    configuration?: ITrendChartReportConfiguration
): ChartProps {
    // Adjust data to fit the days filter selected by user.
    let adjustableData = data.map((value: IDailyTaskFailureData) => {
        return {
            date: value.date,
            data: value.taskFailures
        } as IDataForDate<ITaskFailure[]>;
    });

    adjustableData = adjustDataForAllPeriod(
        adjustableData,
        configuration ? configuration.trendBy : undefined,
        configuration ? configuration.period : undefined,
        undefined
    );
    const adjustedValues = adjustableData.map(datum => datum.date);

    const taskToDataMap: { [taskName: string]: ITaskDataValue } = {};
    let taskValues: string[] = [];
    adjustableData.forEach(datum => {
        if (datum.data) {
            datum.data.forEach(taskFailure => {
                // Build a task name to failure data map to:
                // a. Identify tasks with higher failure count. This is used to just consider the top N tasks while showing chart.
                // b. Reference index of Y axis category item from series data.
                if (!taskToDataMap[taskFailure.taskName]) {
                    taskToDataMap[taskFailure.taskName] = {
                        totalFailures: 0,
                        dailyFailures: {}
                    };

                    taskValues.push(taskFailure.taskName);
                }

                taskToDataMap[taskFailure.taskName].dailyFailures[datum.date] = taskFailure.failedCount;
                taskToDataMap[taskFailure.taskName].totalFailures += taskFailure.failedCount;
            });
        }
    });

    // Sort the tasks based on total failures and just consider top 'taskLimit' tasks
    taskValues = taskValues.sort((a, b) => taskToDataMap[b].totalFailures - taskToDataMap[a].totalFailures).slice(0, maxItemsToShowInCharts);

    let i = 0;
    const series = taskValues.map(taskName => {
        return {
            name: taskName,
            data: adjustedValues.map(date => taskToDataMap[taskName].dailyFailures[date] || null),
            color: colors[i++]
        };
    });

    const chartData = {
        title: Resources.TaskFailureTrendChartTitle,
        xAxisLabelValues: adjustedValues,
        yAxisTitle: Resources.NumberOfFailuresText,
        series: series,
        telemetryArea: TelemetryConstants.feature_PipelineAnalyticsTasksFailedTrendChart
    } as IAnalyticsChartProps;
    return getStackedColumnChartData(pageContext, chartData);
}
